import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Stack,
  Typography,
  TextField,
  Button,
  Divider,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import LocalstorageHelper from "../../LocalStorageHelper/LocalstorageHelper";
import { Redirect } from "react-router-dom";
import Sidebar from "../Sidebar/Sidebar";

export default function CaseItemScanCopyDashboard() {
  const userToken = LocalstorageHelper.GetUserToken();

  const redirectToLoginPage = () => {
    if (!userToken) {
      return <Redirect exact to="/sparepartsLogin" />;
    }
  };

  const [invno, setInvno] = useState("");
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState("items"); // items | dpoSummary | dpoItems | cases | pendingCases
  const [itemFilter, setItemFilter] = useState("all"); // all | partly | short | matched
  const [error, setError] = useState("");

  // ✅ NEW: rack filters (Items view only)
  const [rackChangedFilter, setRackChangedFilter] = useState("all"); // all | changed | unchanged
  const [rackQuery, setRackQuery] = useState(""); // text filter on rackname

  const [header, setHeader] = useState(null);
  const [items, setItems] = useState([]);
  const [dpoSummary, setDpoSummary] = useState([]);
  const [dpoItems, setDpoItems] = useState([]);
  const [cases, setCases] = useState([]);
  const [pendingCases, setPendingCases] = useState([]);

  // reset pagination when tab changes
  const [pageModel, setPageModel] = useState({ page: 0, pageSize: 25 });

  // ✅ Reset page on tab change; reset filters when leaving items
  useEffect(() => {
    setPageModel((p) => ({ ...p, page: 0 }));
    if (view !== "items") {
      setItemFilter("all");
      setRackChangedFilter("all");
      setRackQuery("");
    }
  }, [view]);

  // ✅ Reset page when toggle filters change (NOT on each rack text keystroke)
  useEffect(() => {
    if (view === "items") setPageModel((p) => ({ ...p, page: 0 }));
  }, [view, itemFilter, rackChangedFilter]);

  const fmtNum = useCallback((v) => {
    const n = Number(v ?? 0);
    if (Number.isNaN(n)) return "0.00";
    return n.toFixed(2);
  }, []);

  const n0 = useCallback((v) => {
    const n = Number(v ?? 0);
    return Number.isFinite(n) ? n : 0;
  }, []);

  const isZero = useCallback((v) => Math.abs(n0(v)) < 1e-9, [n0]);

  const fetchDashboard = useCallback(async () => {
    const inv = (invno || "").trim();
    if (!inv) {
      setError("Please enter invno");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const r = await fetch(
        `/caseitemscancopy/dashboard?invno=${encodeURIComponent(inv)}`,
        {
          method: "GET",
          headers: {
            sptoken: userToken,
            "Content-Type": "application/json",
          },
        }
      );

      if (!r.ok) {
        const t = await r.text();
        throw new Error(t || "Failed to load dashboard");
      }

      const json = await r.json();

      setHeader(json.header || null);
      setItems(Array.isArray(json.items) ? json.items : []);
      setDpoSummary(Array.isArray(json.dpoSummary) ? json.dpoSummary : []);
      setDpoItems(Array.isArray(json.dpoItems) ? json.dpoItems : []);
      setCases(Array.isArray(json.cases) ? json.cases : []);
      setPendingCases(Array.isArray(json.pendingCases) ? json.pendingCases : []);
    } catch (e) {
      setHeader(null);
      setItems([]);
      setDpoSummary([]);
      setDpoItems([]);
      setCases([]);
      setPendingCases([]);
      setError(e?.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  }, [invno, userToken]);

  const resetAll = useCallback(() => {
    setInvno("");
    setHeader(null);
    setItems([]);
    setDpoSummary([]);
    setDpoItems([]);
    setCases([]);
    setPendingCases([]);
    setError("");
    setView("items");
    setItemFilter("all");

    // ✅ reset rack filters
    setRackChangedFilter("all");
    setRackQuery("");

    setPageModel({ page: 0, pageSize: 25 });
  }, []);

  /* ---------------- Columns ---------------- */
  const itemCols = useMemo(
    () => [
      { field: "casenumber", headerName: "Case", width: 350 },
      { field: "partno", headerName: "Part No", width: 140 },
      {
        field: "partdescription",
        headerName: "Description",
        flex: 1,
        minWidth: 220,
      },

      // ✅ UPDATED: highlight rackname when rackchanged=true
      {
        field: "rackname",
        headerName: "Rack",
        width: 160,
        sortable: true,
        renderCell: (p) => {
          const name = (p.value || "").trim();
          const changed = Boolean(p.row?.rackchanged);

          if (!name) {
            return changed ? (
              <Chip size="small" label="(NO RACK) • Changed" color="warning" />
            ) : (
              <Chip size="small" label="(NO RACK)" variant="outlined" />
            );
          }

          return changed ? (
            <Chip
              size="small"
              label={`${name} • Changed`}
              color="warning"
              sx={{ fontWeight: 700 }}
            />
          ) : (
            <Chip size="small" label={name} variant="outlined" />
          );
        },
      },

      {
        field: "transitqty",
        headerName: "Transit Qty",
        width: 120,
        type: "number",
        valueFormatter: (p) => fmtNum(p.value),
      },
      {
        field: "allocatedqty",
        headerName: "Allocated Qty (Scan)",
        width: 160,
        type: "number",
        valueFormatter: (p) => fmtNum(p.value),
      },
      {
        field: "dpo_alloc_qty",
        headerName: "DPO Alloc",
        width: 120,
        type: "number",
        valueFormatter: (p) => fmtNum(p.value),
      },
      {
        field: "stock_alloc_qty",
        headerName: "Stock Alloc",
        width: 130,
        type: "number",
        valueFormatter: (p) => fmtNum(p.value),
      },
      {
        field: "balanceqty",
        headerName: "Balance",
        width: 120,
        type: "number",
        valueFormatter: (p) => fmtNum(p.value),
      },
      {
        field: "status",
        headerName: "Status",
        width: 160,
        sortable: false,
        filterable: false,
        renderCell: (p) => {
          const bal = n0(p.row?.balanceqty);
          const alloc = n0(p.row?.allocatedqty);

          if (isZero(bal))
            return (
              <Chip
                size="small"
                label="MATCHED"
                color="success"
                variant="outlined"
              />
            );

          if (bal > 0) {
            if (alloc > 0)
              return (
                <Chip
                  size="small"
                  label="PARTLY ALLOCATED"
                  color="info"
                  variant="outlined"
                />
              );

            return (
              <Chip
                size="small"
                label="SHORT"
                color="warning"
                variant="outlined"
              />
            );
          }

          return (
            <Chip
              size="small"
              label="EXCESS"
              color="error"
              variant="outlined"
            />
          );
        },
      },
    ],
    [fmtNum, n0, isZero]
  );

  const dpoSummaryCols = useMemo(
    () => [
      { field: "dpono", headerName: "DPO No", width: 140 },
      {
        field: "party",
        headerName: "Party",
        flex: 1,
        minWidth: 260,
        valueGetter: (p) => {
          if ((p.row?.dpono || "") === "STOCK") return "STOCK";
          const code = (p.row?.partycode || "").trim();
          const name = (p.row?.partyname || "").trim();
          if (code && name) return `${code} - ${name}`;
          if (name) return name;
          if (code) return code;
          return `Subledger: ${p.row?.subledgerid ?? ""}`;
        },
      },
      {
        field: "dpo_qty",
        headerName: "DPO Qty",
        width: 120,
        type: "number",
        valueFormatter: (p) => fmtNum(p.value),
      },
      {
        field: "alloc_qty",
        headerName: "Allocated",
        width: 120,
        type: "number",
        valueFormatter: (p) => fmtNum(p.value),
      },
      {
        field: "unalloc_qty",
        headerName: "Unallocated",
        width: 130,
        type: "number",
        valueFormatter: (p) => fmtNum(p.value),
      },
      {
        field: "status",
        headerName: "Status",
        width: 150,
        sortable: false,
        filterable: false,
        renderCell: (p) => {
          const u = Number(p.row?.unalloc_qty ?? 0);
          if (u > 0)
            return (
              <Chip
                size="small"
                label="UNALLOCATED"
                color="warning"
                variant="outlined"
              />
            );
          return (
            <Chip size="small" label="OK" color="success" variant="outlined" />
          );
        },
      },
    ],
    [fmtNum]
  );

  const dpoItemCols = useMemo(
    () => [
      { field: "dpono", headerName: "DPO No", width: 140 },
      { field: "partno", headerName: "Part No", width: 140 },
      {
        field: "partdescription",
        headerName: "Description",
        flex: 1,
        minWidth: 240,
      },
      {
        field: "dpo_qty",
        headerName: "DPO Qty",
        width: 120,
        type: "number",
        valueFormatter: (p) => fmtNum(p.value),
      },
      {
        field: "alloc_qty",
        headerName: "Allocated",
        width: 120,
        type: "number",
        valueFormatter: (p) => fmtNum(p.value),
      },
      {
        field: "unalloc_qty",
        headerName: "Unallocated",
        width: 130,
        type: "number",
        valueFormatter: (p) => fmtNum(p.value),
      },
    ],
    [fmtNum]
  );

  const caseCols = useMemo(
    () => [
      { field: "casenumber", headerName: "Case No", flex: 1, minWidth: 220 },
      {
        field: "billty_display",
        headerName: "Billty / Verification",
        flex: 1,
        minWidth: 340,
      },
      {
        field: "verified_at",
        headerName: "Verified At",
        width: 200,
        valueGetter: (p) => p.row?.verified_at || "",
      },
    ],
    []
  );

  const pendingCaseCols = useMemo(
    () => [
      {
        field: "casenumber",
        headerName: "Pending Case No",
        flex: 1,
        minWidth: 260,
      },
    ],
    []
  );

  const itemsRows = useMemo(() => {
    const src = Array.isArray(items) ? items : [];
    return src.map((r, idx) => ({
      ...r,
      __rid:
        r?.__rid ??
        `ITM|${r?.itemid ?? ""}|${r?.partno ?? ""}|${r?.partdescription ?? ""}|${r?.transitqty ?? ""}|${r?.allocatedqty ?? ""}|${r?.balanceqty ?? ""}|${idx}`,
    }));
  }, [items]);

  /* ---------------- Item Filters (Items view) ---------------- */
  const itemCounts = useMemo(() => {
    const all = itemsRows;
    let matched = 0;
    let short = 0;
    let partly = 0;
    let excess = 0;

    for (const r of all) {
      const bal = n0(r?.balanceqty);
      const alloc = n0(r?.allocatedqty);

      if (isZero(bal)) matched += 1;
      else if (bal > 0) {
        if (alloc > 0) partly += 1;
        else short += 1;
      } else excess += 1;
    }

    return { all: all.length, matched, short, partly, excess };
  }, [itemsRows, n0, isZero]);

  // ✅ NEW: rackchanged counts
  const rackCounts = useMemo(() => {
    const all = itemsRows;
    let changed = 0;
    let ok = 0;
    for (const r of all) {
      if (Boolean(r?.rackchanged)) changed += 1;
      else ok += 1;
    }
    return { changed, ok };
  }, [itemsRows]);

  const filteredItems = useMemo(() => {
    let out = itemsRows;

    // status filter
    if (itemFilter === "matched")
      out = out.filter((r) => isZero(n0(r?.balanceqty)));
    else if (itemFilter === "partly")
      out = out.filter(
        (r) => n0(r?.balanceqty) > 0 && n0(r?.allocatedqty) > 0
      );
    else if (itemFilter === "short")
      out = out.filter(
        (r) => n0(r?.balanceqty) > 0 && !(n0(r?.allocatedqty) > 0)
      );

    // ✅ rackchanged filter
    if (rackChangedFilter === "changed")
      out = out.filter((r) => Boolean(r?.rackchanged));
    else if (rackChangedFilter === "unchanged")
      out = out.filter((r) => !Boolean(r?.rackchanged));

    // ✅ rack name text filter
    const q = (rackQuery || "").trim().toLowerCase();
    if (q) {
      out = out.filter((r) =>
        String(r?.rackname ?? "").toLowerCase().includes(q)
      );
    }

    return out;
  }, [itemsRows, itemFilter, rackChangedFilter, rackQuery, n0, isZero]);

  const activeRows = useMemo(() => {
    if (view === "items") return filteredItems;
    if (view === "dpoSummary") return dpoSummary;
    if (view === "dpoItems") return dpoItems;
    if (view === "cases") return cases;
    return pendingCases;
  }, [view, filteredItems, dpoSummary, dpoItems, cases, pendingCases]);

  const activeCols = useMemo(() => {
    if (view === "items") return itemCols;
    if (view === "dpoSummary") return dpoSummaryCols;
    if (view === "dpoItems") return dpoItemCols;
    if (view === "cases") return caseCols;
    return pendingCaseCols;
  }, [view, itemCols, dpoSummaryCols, dpoItemCols, caseCols, pendingCaseCols]);

  const gridRowId = useCallback(
    (row) => {
      if (view === "items") return row.__rid;
      if (view === "dpoSummary")
        return `DPOSUM|${row.dpono ?? ""}|${row.subledgerid ?? 0}`;
      if (view === "dpoItems")
        return `DPOITM|${row.dpono ?? ""}|${row.subledgerid ?? 0}|${row.itemid ?? 0}|${row.partno ?? ""}`;
      if (view === "cases")
        return `CASE|${row.casenumber ?? ""}|${row.invno ?? row.invoicenumber ?? ""}`;
      return `PENDING|${row.casenumber ?? ""}`;
    },
    [view]
  );

  const totalTransit = Number(header?.transit_total_qty ?? 0);
  const totalAlloc = Number(header?.allocated_total_qty ?? 0);
  const totalBal = totalTransit - totalAlloc;
  const totalDpoAlloc = Number(header?.dpo_alloc_total ?? 0);
  const totalStockAlloc = Number(header?.stock_alloc_total ?? 0);

  return (
    <div>
      <Sidebar
        Title={"Case Item Scan Copy Dashboard"}
        expandedPanel={"panel2"}
        children={
          <Box p={2}>
            <Stack spacing={2}>
              {/* Search Card */}
              <Card>
                <CardContent>
                  <Stack
                    direction={{ xs: "column", md: "row" }}
                    spacing={2}
                    alignItems="center"
                  >
                    <TextField
                      label="Transit Invoice No (invno)"
                      value={invno}
                      onChange={(e) => setInvno(e.target.value)}
                      fullWidth
                      size="small"
                      onKeyDown={(e) => {
                        if (e.key === "Enter") fetchDashboard();
                      }}
                    />
                    <Stack direction="row" spacing={1}>
                      <Button
                        variant="contained"
                        startIcon={
                          loading ? (
                            <CircularProgress size={18} color="inherit" />
                          ) : (
                            <SearchIcon />
                          )
                        }
                        onClick={fetchDashboard}
                        disabled={loading}
                      >
                        Search
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<RefreshIcon />}
                        onClick={resetAll}
                        disabled={loading}
                      >
                        Reset
                      </Button>
                    </Stack>
                  </Stack>

                  {error ? (
                    <Typography mt={1} color="error" variant="body2">
                      {error}
                    </Typography>
                  ) : null}

                  {/* Header Summary */}
                  {header ? (
                    <>
                      <Divider sx={{ my: 2 }} />
                      <Stack
                        direction={{ xs: "column", md: "row" }}
                        spacing={1}
                        alignItems="center"
                        flexWrap="wrap"
                      >
                        <Chip
                          label={`INVNO: ${header.invno || ""}`}
                          variant="outlined"
                        />
                        <Chip
                          label={`Inv Date: ${header.invdate || "-"}`}
                          variant="outlined"
                        />
                        <Chip
                          label={`Transit Qty: ${fmtNum(totalTransit)}`}
                          variant="outlined"
                        />
                        <Chip
                          label={`Allocated Qty (Scan): ${fmtNum(totalAlloc)}`}
                          variant="outlined"
                        />
                        <Chip
                          label={`DPO Alloc: ${fmtNum(totalDpoAlloc)}`}
                          variant="outlined"
                        />
                        <Chip
                          label={`Stock Alloc: ${fmtNum(totalStockAlloc)}`}
                          variant="outlined"
                        />
                        <Chip
                          label={`Balance: ${fmtNum(totalBal)}`}
                          color={totalBal === 0 ? "success" : "warning"}
                          variant="outlined"
                        />
                        <Chip
                          label={`Transit Items: ${header.transit_item_count || 0}`}
                          variant="outlined"
                        />
                        <Chip
                          label={`Allocated Items: ${header.allocated_item_count || 0}`}
                          variant="outlined"
                        />
                        <Chip
                          label={`Cases: ${header.case_count || 0}`}
                          variant="outlined"
                        />
                        <Chip
                          label={`Pending Cases: ${
                            Array.isArray(pendingCases) ? pendingCases.length : 0
                          }`}
                          color={(pendingCases?.length ?? 0) > 0 ? "warning" : "success"}
                          variant="outlined"
                        />
                      </Stack>
                    </>
                  ) : null}
                </CardContent>
              </Card>

              {/* View Toggle */}
              <Card>
                <CardContent>
                  <Stack spacing={1}>
                    <Typography variant="subtitle2">
                      View (invno-based. Allocated Qty comes from
                      t_caseitemscan_copy.allocqty. No pickqty.)
                    </Typography>
                    <ToggleButtonGroup
                      value={view}
                      exclusive
                      onChange={(e, v) => v && setView(v)}
                      size="small"
                    >
                      <ToggleButton value="items">Item Summary</ToggleButton>
                      <ToggleButton value="dpoSummary">DPO Summary</ToggleButton>
                      <ToggleButton value="dpoItems">DPO Items</ToggleButton>
                      <ToggleButton value="cases">Case Verification</ToggleButton>
                      <ToggleButton value="pendingCases">Pending Cases</ToggleButton>
                    </ToggleButtonGroup>
                  </Stack>
                </CardContent>
              </Card>

              {/* Item Filters */}
              {view === "items" ? (
                <Card>
                  <CardContent>
                    <Stack spacing={2}>
                      <Stack
                        direction={{ xs: "column", md: "row" }}
                        spacing={1}
                        alignItems={{ xs: "stretch", md: "center" }}
                        justifyContent="space-between"
                      >
                        <Stack
                          direction="row"
                          spacing={1}
                          flexWrap="wrap"
                          alignItems="center"
                        >
                          <Typography variant="subtitle2" sx={{ mr: 1 }}>
                            Status
                          </Typography>
                          <ToggleButtonGroup
                            value={itemFilter}
                            exclusive
                            onChange={(e, v) => v && setItemFilter(v)}
                            size="small"
                          >
                            <ToggleButton value="all">
                              All ({itemCounts.all})
                            </ToggleButton>
                            <ToggleButton value="matched">
                              Matched ({itemCounts.matched})
                            </ToggleButton>
                            <ToggleButton value="partly">
                              Partly ({itemCounts.partly})
                            </ToggleButton>
                            <ToggleButton value="short">
                              Short ({itemCounts.short})
                            </ToggleButton>
                          </ToggleButtonGroup>
                        </Stack>

                        <Typography variant="caption" color="text.secondary">
                          Partly Allocated = Allocated Qty &gt; 0 &amp; Balance
                          &gt; 0
                        </Typography>
                      </Stack>

                      {/* ✅ NEW: Rack filters row */}
                      <Stack
                        direction={{ xs: "column", md: "row" }}
                        spacing={1}
                        alignItems={{ xs: "stretch", md: "center" }}
                      >
                        <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
                          <Typography variant="subtitle2" sx={{ mr: 1 }}>
                            Rack
                          </Typography>

                          <ToggleButtonGroup
                            value={rackChangedFilter}
                            exclusive
                            onChange={(e, v) => v && setRackChangedFilter(v)}
                            size="small"
                          >
                            <ToggleButton value="all">All</ToggleButton>
                            <ToggleButton value="changed">
                              Rack Changed ({rackCounts.changed})
                            </ToggleButton>
                            <ToggleButton value="unchanged">
                              Rack OK ({rackCounts.ok})
                            </ToggleButton>
                          </ToggleButtonGroup>
                        </Stack>

                        <TextField
                          label="Rack name filter"
                          value={rackQuery}
                          onChange={(e) => setRackQuery(e.target.value)}
                          size="small"
                          sx={{ minWidth: 240 }}
                        />
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>
              ) : null}

              {/* Grid */}
              <Card>
                <CardContent>
                  <Box sx={{ height: 560, width: "100%" }}>
                    <DataGrid
                      key={`${view}-${view === "items" ? `${itemFilter}-${rackChangedFilter}` : ""}`}
                      rows={activeRows}
                      columns={activeCols}
                      getRowId={gridRowId}
                      loading={loading}
                      slots={{ toolbar: GridToolbar }}
                      slotProps={{
                        toolbar: {
                          showQuickFilter: true,
                          quickFilterProps: { debounceMs: 250 },
                        },
                      }}
                      disableRowSelectionOnClick
                      density="compact"
                      pageSizeOptions={[10, 25, 50, 100]}
                      paginationModel={pageModel}
                      onPaginationModelChange={setPageModel}
                      initialState={{
                        pagination: {
                          paginationModel: { pageSize: 25, page: 0 },
                        },
                      }}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Stack>
          </Box>
        }
      />
      {redirectToLoginPage()}
    </div>
  );
}